/**
 * Servicio de persistencia real para conductores y viajes usando el backend Django.
 */
import { apiClient } from './client';
import type { QueryParams } from './client';
import { generarEnlaceSubidaMinio } from './parametros';
import type {
  CargaGasolina,
  Cliente,
  Conductor,
  FormaPagoCargaGasolina,
  GuiaDevolucionViaje,
  InicioDia,
  PagoEntrega,
  PagoPaquete,
  PuntoViaje,
  GuiaFirmadaViaje,
  GuiaPdfViaje,
  Sucursal,
  TransferenciaViaje,
  Viaje,
  Coordenada,
  Vehiculo,
  EtiquetaVehiculo,
  FormaPagoEntrega,
  TipoCliente,
  TipoGasolinaVehiculo,
  TipoEntregaViaje,
  VendedorResumen,
} from './appTypes';

interface ApiEnvelope<T> {
  code: number;
  status: string;
  message: string;
  data: T;
}

interface BackendConductor {
  id: number;
  nombre: string;
  identificacion?: string | null;
  placa?: string | null;
  telefono: string;
  correo: string;
  marca_vehiculo?: string | null;
  modelo_vehiculo?: string | null;
  anio_vehiculo?: number | null;
  username?: string;
  user_id?: number;
  activo?: boolean;
  vehiculo_id?: number | null;
  sucursal?: BackendSucursal | null;
  vehiculo?: BackendVehiculo | null;
  vehiculos?: BackendVehiculo[] | null;
}

interface BackendVehiculo {
  id: number;
  placa: string;
  marca: string;
  modelo: string;
  anio: number;
  tipo_gasolina?: string;
  kilometros_galon?: number | null;
  precio_galon?: number | null;
  etiqueta: string;
  disponible: boolean;
  pico_y_placa_activo?: boolean;
  pico_y_placa_detalle?: {
    activo: boolean;
    dia: string;
    horario: string;
    hora_quito: string;
    placa: string;
    marca?: string;
    modelo?: string;
    mensaje: string;
  } | null;
  motivo_no_disponible?: 'etiqueta' | 'asignado' | 'pico_y_placa' | '';
  sucursal?: BackendSucursal | null;
  conductor?: {
    id: number;
    nombre: string;
    activo: boolean;
  } | null;
}

interface BackendCoordinate {
  lat: number;
  lng: number;
  timestamp: string;
}

interface BackendGasolina {
  tipo: string;
  galones: number;
  precio_por_galon: number;
  total?: number;
}

interface BackendPuntoViaje {
  url_destino: string;
  latitud?: number | null;
  longitud?: number | null;
}

interface BackendPaquete {
  nombre: string;
  descripcion: string;
  imagenes: string[];
  cliente?: BackendCliente | null;
  guia_pdf_url?: string;
  guia_pdf_nombre_archivo?: string;
  envio_pdf_url?: string;
  envio_pdf_nombre_archivo?: string;
  guias_pdf?: BackendGuiaPdf[] | null;
  valor_total_factura?: number | null;
  pago?: BackendPagoPaquete | null;
}

interface BackendGuiaPdfItem {
  codigo: string;
  descripcion: string;
  cantidad?: number | null;
  precio_unitario?: number | null;
  precio_total?: number | null;
}

interface BackendGuiaPdfExtraccion {
  total?: number | null;
  items?: BackendGuiaPdfItem[] | null;
}

interface BackendGuiaPdf {
  url: string;
  nombre_archivo?: string | null;
  numero_factura?: string | null;
  direccion?: string | null;
  valor_total?: number | null;
  es_devuelta?: boolean | null;
  devolucion_total?: number | null;
  tipo_devolucion?: string | null;
  extraccion?: BackendGuiaPdfExtraccion | null;
  items_devueltos?: BackendGuiaPdfItem[] | null;
}

interface BackendPagoPaquete {
  tipo: string;
  monto?: number | null;
  banco?: string | null;
  numero_transferencia?: string | null;
  numero_cheque?: string | null;
  numero_nota_credito?: string | null;
  valor_nota_credito?: number | null;
  tipo_pago_parcial?: string | null;
  monto_pagado?: number | null;
  medio_pago?: string | null;
}

interface BackendPagoEntrega {
  tipo: string;
  monto?: number | null;
  banco?: string | null;
  numero_transferencia?: string | null;
  numero_cheque?: string | null;
  numero_nota_credito?: string | null;
  valor_nota_credito?: number | null;
}

interface BackendCliente {
  id?: number;
  nombre: string;
  numero?: string;
  fecha_documento?: string;
  ciudad?: string;
  sucursal?: string;
  correo_electronico: string;
  direccion?: string;
  telefono?: string;
  tipo_cliente?: Exclude<TipoCliente, ''> | null;
}

interface BackendVendedor {
  id: number;
  username: string;
  nombre: string;
  correo?: string;
  sucursal?: BackendSucursal | null;
  origen?: string;
}

interface BackendSucursal {
  id: number;
  nombre: string;
  direccion: string;
  telefono: string;
}

interface BackendInicioDia {
  id: number;
  conductor_id: number;
  fecha_registro: string;
  cantidad_gasolina: number;
  precio_total: number;
}

interface BackendCargaGasolina {
  id: number;
  conductor_id: number;
  conductor_nombre?: string;
  vehiculo_id: number;
  vehiculo?: BackendVehiculo | null;
  forma_pago: FormaPagoCargaGasolina;
  ultimos_cuatro_tarjeta?: string | null;
  numero_voucher: string;
  img_facturavou?: string | null;
  cantidad_galones: number;
  total_pagado: number;
  fecha_registro: string;
}

export interface BackendViaje {
  id: number;
  created_at?: string | null;
  conductor_id?: number | null;
  conductor_nombre?: string | null;
  conductor_secundario_id?: number | null;
  conductor_secundario_nombre?: string | null;
  vehiculo_id?: number | null;
  vehiculo_secundario_id?: number | null;
  vehiculo?: BackendVehiculo | null;
  estado: 'pendiente' | 'en_curso' | 'completado';
  es_devolucion?: boolean;
  tipo_devolucion?: string | null;
  vendedor?: BackendVendedor | null;
  sucursal?: BackendSucursal | null;
  paquete?: BackendPaquete;
  tipo_entrega?: string | null;
  transferencia?: {
    numero?: string | null;
    fecha_documento?: string | null;
    bodega_origen?: string | null;
    bodega_destino?: string | null;
    transportista?: string | null;
    vehiculo?: string | null;
    chofer?: string | null;
    descripcion?: string | null;
    total_productos?: number | null;
    total_items?: number | null;
    total_unidades?: number | null;
    items?: Array<{
      codigo: string;
      codigo_comercial?: string | null;
      marca_repuesto?: string | null;
      producto: string;
      cantidad?: number | null;
    }> | null;
  } | null;
  compania_transporte?: string | null;
  valor_total_envio?: number | null;
  fee_envio?: number | null;
  url_destino?: string | null;
  puntos_intermedios?: BackendPuntoViaje[] | null;
  tiempo_estimado_minutos?: number | null;
  coordenadas: BackendCoordinate[];
  coordenada_actual?: BackendCoordinate | null;
  coordenada_inicio?: BackendCoordinate | null;
  coordenada_fin?: BackendCoordinate | null;
  direccion_entrega?: string | null;
  gasolina?: BackendGasolina | null;
  forma_pago?: string | null;
  pago_entrega?: BackendPagoEntrega | null;
  monto_cobro_viaje?: number | null;
  referencia_pago?: string | null;
  kilometros_recorridos?: number | null;
  total_viaje?: number | null;
  imagen_entrega?: string | null;
  imagenes_entrega?: string[] | null;
  pdf_guia_firmado_url?: string | null;
  guias_firmadas?: BackendGuiaPdf[] | null;
  observaciones?: string | null;
  fecha_inicio?: string | null;
  fecha_fin?: string | null;
}

interface SaveViajeOptions {
  coordenadaInicio?: Coordenada;
  coordenadaFin?: Coordenada;
  pdfGuiaFirmado?: GuiaFileUpload;
  guiasDevolucion?: GuiaDevolucionViaje[];
  fullEdit?: boolean;
  guiaProcesada?: {
    fileName?: string;
    numeroFactura?: string;
    direccion?: string;
    extraccion: GuiaProcesadaExtraccion;
  };
  guiasProcesadas?: Array<{
    fileName?: string;
    numeroFactura?: string;
    direccion?: string;
    extraccion: GuiaProcesadaExtraccion;
  }>;
}

export interface GuiaFileUpload {
  fileName: string;
  content: string;
  tipoEntrega?: TipoEntregaViaje;
}

export interface GuiaProcesadaItem {
  codigo: string;
  descripcion: string;
  cantidad?: number | null;
  precioUnitario?: number | null;
  precioTotal?: number | null;
}

export interface GuiaProcesadaTransferenciaItem {
  codigo: string;
  codigoComercial?: string;
  marcaRepuesto?: string;
  producto: string;
  cantidad?: number | null;
}

export interface GuiaProcesadaTransferencia {
  numero: string;
  fecha: string;
  bodegaOrigen: string;
  bodegaDestino: string;
  transportista: string;
  vehiculo: string;
  chofer: string;
  descripcion: string;
  totalProductos?: number | null;
  totalItems?: number | null;
  totalUnidades?: number | null;
  items: GuiaProcesadaTransferenciaItem[];
}

export interface GuiaProcesadaExtraccion {
  tipoDocumento: string;
  telefono: string;
  direccion: string;
  emisorRuc: string;
  claveAcceso: string;
  subtotal?: number | null;
  iva?: number | null;
  total?: number | null;
  items: GuiaProcesadaItem[];
  transferencia?: GuiaProcesadaTransferencia;
  headerText: string;
  bodyText: string;
  footerText: string;
  textoExtraido: string;
}

export interface GuiaProcesada {
  guiaPdfUrl: string;
  guiaPdfNombreArchivo: string;
  cliente: Cliente;
  suggestedUrlDestino?: string;
  historicalDestinationUrls?: string[];
  extraccion: GuiaProcesadaExtraccion;
}
type ProcessedGuiaResponse = {
  guia_pdf_url: string;
  guia_pdf_nombre_archivo: string;
  cliente: BackendCliente;
  url_destino_sugerido?: string;
  urls_destino_historicas?: string[];
  extraccion: {
    tipo_documento: string;
    telefono: string;
    direccion: string;
    emisor_ruc: string;
    clave_acceso: string;
    subtotal?: number | null;
    iva?: number | null;
    total?: number | null;
    items: Array<{
      codigo: string;
      descripcion: string;
      cantidad?: number | null;
      precio_unitario?: number | null;
      precio_total?: number | null;
    }>;
    transferencia?: {
      numero?: string | null;
      fecha?: string | null;
      bodega_origen?: string | null;
      bodega_destino?: string | null;
      transportista?: string | null;
      vehiculo?: string | null;
      chofer?: string | null;
      descripcion?: string | null;
      total_productos?: number | null;
      total_items?: number | null;
      total_unidades?: number | null;
      items?: Array<{
        codigo: string;
        codigo_comercial?: string | null;
        marca_repuesto?: string | null;
        producto: string;
        cantidad?: number | null;
      }> | null;
    } | null;
    header_text: string;
    body_text: string;
    footer_text: string;
    texto_extraido: string;
  };
};

export interface GuiaFirmada {
  pdfGuiaFirmadoUrl: string;
  pdfGuiaFirmadoNombreArchivo: string;
}

export interface DestinoVerificado {
  latitud: number;
  longitud: number;
}

export interface PaginatedResult<T> {
  items: T[];
  count: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

type DashboardTrendTone = 'up' | 'down' | 'neutral';
type DashboardFeedTone = 'red' | 'black' | 'green' | 'yellow';

interface BackendDashboardStat {
  icon: string;
  label: string;
  value: string;
  trend: string;
  trend_tone?: DashboardTrendTone;
}

interface BackendDashboardFeedItem {
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  timestamp?: string | null;
  tone?: DashboardFeedTone;
}

interface BackendDashboardStats {
  summary: BackendDashboardStat[];
  weekly_series: Array<{
    label: string;
    value: number;
  }>;
  recent_activity: BackendDashboardFeedItem[];
  notifications: BackendDashboardFeedItem[];
}

export interface DashboardStat {
  icon: string;
  label: string;
  value: string;
  trend: string;
  trendTone: DashboardTrendTone;
}

export interface DashboardFeedItem {
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  meta: string;
  tone?: DashboardFeedTone;
}

export interface DashboardStatsData {
  summary: DashboardStat[];
  weeklySeries: Array<{
    label: string;
    value: number;
  }>;
  recentActivity: DashboardFeedItem[];
  notifications: DashboardFeedItem[];
}

export const EMPTY_DASHBOARD_STATS: DashboardStatsData = {
  summary: [],
  weeklySeries: [],
  recentActivity: [],
  notifications: [],
};

export interface CreateInicioDiaInput {
  conductorId?: string;
  cantidadGasolina: number;
  precioTotal: number;
}

export interface CreateCargaGasolinaInput {
  vehiculoId: string;
  formaPago: FormaPagoCargaGasolina;
  ultimosCuatroTarjeta?: string;
  numeroVoucher: string;
  imgFacturavou?: string;
  cantidadGalones: number;
  totalPagado: number;
}

export interface UpdateCargaGasolinaInput {
  vehiculoId: string;
  formaPago: FormaPagoCargaGasolina;
  ultimosCuatroTarjeta?: string;
  numeroVoucher: string;
  cantidadGalones: number;
  totalPagado: number;
}

export interface CreateConductorInput {
  existingUserId?: number;
  username?: string;
  password?: string;
  email?: string;
  nombre: string;
  telefono: string;
  placa?: string;
  marcaVehiculo?: string;
  modeloVehiculo?: string;
  anioVehiculo?: number;
  sucursalId?: string | null;
}

export interface UpsertVehiculoInput {
  placa: string;
  marca: string;
  modelo: string;
  anio: number;
  tipoGasolina: TipoGasolinaVehiculo;
  kilometrosGalon: number;
  precioGalon: number;
  etiqueta?: EtiquetaVehiculo;
  sucursalId?: string | null;
  conductorId?: string | null;
}

export interface UpsertSucursalInput {
  nombre: string;
  direccion: string;
  telefono: string;
}

function mapVehiculo(vehiculo: BackendVehiculo): Vehiculo {
  return {
    id: String(vehiculo.id),
    placa: vehiculo.placa,
    marca: vehiculo.marca,
    modelo: vehiculo.modelo,
    anio: vehiculo.anio,
    tipoGasolina: (vehiculo.tipo_gasolina || '') as TipoGasolinaVehiculo | '',
    kilometrosGalon: vehiculo.kilometros_galon ?? undefined,
    precioGalon: vehiculo.precio_galon ?? undefined,
    etiqueta: (vehiculo.etiqueta || '') as EtiquetaVehiculo,
    disponible: vehiculo.disponible,
    sucursal: vehiculo.sucursal ? mapSucursal(vehiculo.sucursal) : null,
    picoYPlacaActivo: Boolean(vehiculo.pico_y_placa_activo),
    picoYPlacaDetalle: vehiculo.pico_y_placa_detalle
      ? {
          activo: Boolean(vehiculo.pico_y_placa_detalle.activo),
          dia: vehiculo.pico_y_placa_detalle.dia,
          horario: vehiculo.pico_y_placa_detalle.horario,
          horaQuito: vehiculo.pico_y_placa_detalle.hora_quito,
          placa: vehiculo.pico_y_placa_detalle.placa,
          marca: vehiculo.pico_y_placa_detalle.marca,
          modelo: vehiculo.pico_y_placa_detalle.modelo,
          mensaje: vehiculo.pico_y_placa_detalle.mensaje,
        }
      : null,
    motivoNoDisponible: vehiculo.motivo_no_disponible ?? '',
    conductor: vehiculo.conductor
      ? {
          id: String(vehiculo.conductor.id),
          nombre: vehiculo.conductor.nombre,
          activo: vehiculo.conductor.activo,
        }
      : null,
  };
}

function mapCliente(cliente: BackendCliente): Cliente {
  return {
    id: cliente.id ? String(cliente.id) : undefined,
    nombre: cliente.nombre,
    numero: cliente.numero ?? undefined,
    fechaDocumento: cliente.fecha_documento ?? undefined,
    ciudad: cliente.ciudad ?? undefined,
    sucursal: cliente.sucursal ?? undefined,
    correoElectronico: cliente.correo_electronico,
    direccion: cliente.direccion ?? undefined,
    telefono: cliente.telefono ?? undefined,
    tipoCliente: (cliente.tipo_cliente as TipoCliente | null | undefined) ?? '',
  };
}

function mapVendedor(vendedor: BackendVendedor): VendedorResumen {
  return {
    id: vendedor.id,
    username: vendedor.username,
    nombre: vendedor.nombre,
    correo: vendedor.correo,
    sucursal: vendedor.sucursal ? mapSucursal(vendedor.sucursal) : null,
    esErp: vendedor.origen === 'ERP',
  };
}

function mapSucursal(sucursal: BackendSucursal): Sucursal {
  return {
    id: String(sucursal.id),
    nombre: sucursal.nombre,
    direccion: sucursal.direccion,
    telefono: sucursal.telefono,
  };
}

function mapInicioDia(inicioDia: BackendInicioDia): InicioDia {
  return {
    id: String(inicioDia.id),
    conductorId: String(inicioDia.conductor_id),
    fechaRegistro: Date.parse(inicioDia.fecha_registro),
    cantidadGasolina: inicioDia.cantidad_gasolina,
    precioTotal: inicioDia.precio_total,
  };
}

function mapCargaGasolina(cargaGasolina: BackendCargaGasolina): CargaGasolina {
  return {
    id: String(cargaGasolina.id),
    conductorId: String(cargaGasolina.conductor_id),
    conductorNombre: cargaGasolina.conductor_nombre ?? undefined,
    vehiculoId: String(cargaGasolina.vehiculo_id),
    vehiculo: cargaGasolina.vehiculo ? mapVehiculo(cargaGasolina.vehiculo) : null,
    formaPago: cargaGasolina.forma_pago,
    ultimosCuatroTarjeta: cargaGasolina.ultimos_cuatro_tarjeta ?? undefined,
    numeroVoucher: cargaGasolina.numero_voucher,
    imgFacturavou: cargaGasolina.img_facturavou ?? undefined,
    cantidadGalones: cargaGasolina.cantidad_galones,
    totalPagado: cargaGasolina.total_pagado,
    fechaRegistro: Date.parse(cargaGasolina.fecha_registro),
  };
}

function mapConductor(conductor: BackendConductor): Conductor {
  return {
    id: String(conductor.id),
    nombre: conductor.nombre,
    identificacion: conductor.identificacion ?? undefined,
    placa: conductor.placa,
    telefono: conductor.telefono,
    correo: conductor.correo,
    marcaVehiculo: conductor.marca_vehiculo,
    modeloVehiculo: conductor.modelo_vehiculo,
    anioVehiculo: conductor.anio_vehiculo,
    username: conductor.username,
    userId: conductor.user_id,
    activo: conductor.activo,
    vehiculoId: conductor.vehiculo_id,
    sucursal: conductor.sucursal ? mapSucursal(conductor.sucursal) : null,
    vehiculo: conductor.vehiculo ? mapVehiculo(conductor.vehiculo) : null,
    vehiculos: (conductor.vehiculos ?? []).map(mapVehiculo),
  };
}

function mapEstado(estado: BackendViaje['estado']): Viaje['estado'] {
  if (estado === 'completado') return 'finalizado';
  return estado;
}

function mapCoordinate(coord: BackendCoordinate): Coordenada {
  return {
    lat: coord.lat,
    lng: coord.lng,
    timestamp: coord.timestamp ? Date.parse(coord.timestamp) : Date.now(),
  };
}

function mapPuntoViaje(point: BackendPuntoViaje): PuntoViaje {
  return {
    urlDestino: point.url_destino,
    latitud: point.latitud ?? undefined,
    longitud: point.longitud ?? undefined,
  };
}

function mapGuiaPdfItem(item: BackendGuiaPdfItem) {
  return {
    codigo: item.codigo,
    descripcion: item.descripcion,
    cantidad: item.cantidad ?? undefined,
    precioUnitario: item.precio_unitario ?? undefined,
    precioTotal: item.precio_total ?? undefined,
  };
}

function mapGuiaPdf(guia: BackendGuiaPdf): GuiaPdfViaje {
  return {
    url: guia.url,
    nombreArchivo: guia.nombre_archivo ?? undefined,
    numeroFactura: guia.numero_factura ?? undefined,
    direccion: guia.direccion ?? undefined,
    valorTotal: guia.valor_total ?? undefined,
    esDevuelta: guia.es_devuelta ?? undefined,
    devolucionCompleta: guia.es_devuelta ?? undefined,
    devolucionTotal: guia.devolucion_total ?? undefined,
    tipoDevolucion: (guia.tipo_devolucion as GuiaPdfViaje['tipoDevolucion'] | null | undefined) ?? undefined,
    extraccion: guia.extraccion
      ? {
          total: guia.extraccion.total ?? undefined,
          items: (guia.extraccion.items ?? []).map(mapGuiaPdfItem),
        }
      : undefined,
    itemsDevueltos: (guia.items_devueltos ?? []).map(mapGuiaPdfItem),
  };
}

function mapGuiaFirmada(guia: BackendGuiaPdf): GuiaFirmadaViaje {
  return {
    url: guia.url,
    nombreArchivo: guia.nombre_archivo ?? undefined,
    numeroFactura: guia.numero_factura ?? undefined,
    direccion: guia.direccion ?? undefined,
    valorTotal: guia.valor_total ?? undefined,
    esDevuelta: guia.es_devuelta ?? undefined,
  };
}

function toBackendGuiaFirmada(guia: GuiaFirmadaViaje): BackendGuiaPdf {
  return {
    url: guia.url,
    nombre_archivo: guia.nombreArchivo,
    numero_factura: guia.numeroFactura,
    direccion: guia.direccion,
    valor_total: guia.valorTotal,
    es_devuelta: guia.esDevuelta,
  };
}

function toBackendGuiaDevolucion(guia: GuiaDevolucionViaje): BackendGuiaPdf {
  return {
    url: guia.url,
    nombre_archivo: guia.nombreArchivo,
    numero_factura: guia.numeroFactura,
    direccion: guia.direccion,
    valor_total: guia.valorTotal,
    es_devuelta: guia.esDevuelta,
    items_devueltos: guia.itemsDevueltos?.map(item => ({
      codigo: item.codigo,
      descripcion: item.descripcion,
      cantidad: item.cantidad,
      precio_unitario: item.precioUnitario,
      precio_total: item.precioTotal,
    })),
  };
}

export function mapViaje(viaje: BackendViaje): Viaje {
  const gasolina = viaje.gasolina;
  const pago = viaje.paquete?.pago;
  const pagoEntrega = viaje.pago_entrega;

  return {
    id: String(viaje.id),
    createdAt: viaje.created_at ? Date.parse(viaje.created_at) : undefined,
    conductorId: viaje.conductor_id != null ? String(viaje.conductor_id) : undefined,
    conductorNombre: viaje.conductor_nombre ?? undefined,
    secondaryConductorId: viaje.conductor_secundario_id != null ? String(viaje.conductor_secundario_id) : undefined,
    secondaryConductorNombre: viaje.conductor_secundario_nombre ?? undefined,
    vehiculoId: viaje.vehiculo_id != null ? String(viaje.vehiculo_id) : undefined,
    secondaryVehiculoId: viaje.vehiculo_secundario_id != null ? String(viaje.vehiculo_secundario_id) : undefined,
    vehiculo: viaje.vehiculo ? mapVehiculo(viaje.vehiculo) : null,
    estado: mapEstado(viaje.estado),
    esDevolucion: Boolean(viaje.es_devolucion),
    tipoDevolucion: (viaje.tipo_devolucion as Viaje['tipoDevolucion'] | null | undefined) ?? undefined,
    paquete: viaje.paquete
      ? {
          nombre: viaje.paquete.nombre,
          descripcion: viaje.paquete.descripcion,
          imagenes: viaje.paquete.imagenes,
          cliente: viaje.paquete.cliente ? mapCliente(viaje.paquete.cliente) : undefined,
          guiaPdfUrl: viaje.paquete.guia_pdf_url || undefined,
          guiaPdfNombreArchivo: viaje.paquete.guia_pdf_nombre_archivo || undefined,
          envioPdfUrl: viaje.paquete.envio_pdf_url || undefined,
          envioPdfNombreArchivo: viaje.paquete.envio_pdf_nombre_archivo || undefined,
          guiasPdf: (viaje.paquete.guias_pdf ?? []).map(mapGuiaPdf),
          valorTotalFactura: viaje.paquete.valor_total_factura ?? undefined,
          pago: pago
            ? {
                tipo: (pago.tipo || '') as PagoPaquete['tipo'],
                monto: pago.monto ?? undefined,
                banco: pago.banco ?? undefined,
                numeroTransferencia: pago.numero_transferencia ?? undefined,
                numeroCheque: pago.numero_cheque ?? undefined,
                numeroNotaCredito: pago.numero_nota_credito ?? undefined,
                valorNotaCredito: pago.valor_nota_credito ?? undefined,
                tipoPagoParcial: (pago.tipo_pago_parcial || '') as PagoPaquete['tipoPagoParcial'],
                montoPagado: pago.monto_pagado ?? undefined,
                medioPago: (pago.medio_pago || '') as PagoPaquete['medioPago'],
              }
            : undefined,
        }
      : undefined,
    vendedor: viaje.vendedor ? mapVendedor(viaje.vendedor) : undefined,
    sucursal: viaje.sucursal ? mapSucursal(viaje.sucursal) : undefined,
    tipoEntrega: (viaje.tipo_entrega as TipoEntregaViaje | null | undefined) ?? undefined,
    transferencia: mapTransferencia(viaje.transferencia),
    companiaTransporte: viaje.compania_transporte ?? undefined,
    valorTotalEnvio: viaje.valor_total_envio ?? undefined,
    feeEnvio: viaje.fee_envio ?? undefined,
    urlDestino: viaje.url_destino ?? undefined,
    puntosIntermedios: (viaje.puntos_intermedios ?? []).map(mapPuntoViaje),
    tiempoEstimadoMinutos: viaje.tiempo_estimado_minutos ?? undefined,
    coordenadas: viaje.coordenadas.map(mapCoordinate),
    coordenadaActual: viaje.coordenada_actual
      ? mapCoordinate(viaje.coordenada_actual)
      : undefined,
    coordenadaInicio: viaje.coordenada_inicio
      ? mapCoordinate(viaje.coordenada_inicio)
      : undefined,
    coordenadaFin: viaje.coordenada_fin
      ? mapCoordinate(viaje.coordenada_fin)
      : undefined,
    direccionEntrega: viaje.direccion_entrega ?? undefined,
    gasolina: gasolina
      ? {
          tipo: gasolina.tipo as 'EXTRA' | 'SUPER' | 'ECO_PAIS' | 'DIESEL',
          galones: gasolina.galones,
          precioPorGalon: gasolina.precio_por_galon,
          total: gasolina.total,
        }
      : undefined,
    formaPago: (viaje.forma_pago as FormaPagoEntrega | null | undefined) ?? undefined,
    pagoEntrega: pagoEntrega
      ? {
          tipo: (pagoEntrega.tipo || '') as PagoEntrega['tipo'],
          monto: pagoEntrega.monto ?? undefined,
          banco: pagoEntrega.banco ?? undefined,
          numeroTransferencia: pagoEntrega.numero_transferencia ?? undefined,
          numeroCheque: pagoEntrega.numero_cheque ?? undefined,
          numeroNotaCredito: pagoEntrega.numero_nota_credito ?? undefined,
          valorNotaCredito: pagoEntrega.valor_nota_credito ?? undefined,
        }
      : undefined,
    montoCobroViaje: viaje.monto_cobro_viaje ?? undefined,
    referenciaPago: viaje.referencia_pago ?? undefined,
    kilometrosRecorridos: viaje.kilometros_recorridos ?? undefined,
    totalViaje: viaje.total_viaje ?? undefined,
    imagenEntrega: viaje.imagen_entrega ?? undefined,
    imagenesEntrega: viaje.imagenes_entrega ?? undefined,
    pdfGuiaFirmadoUrl: viaje.pdf_guia_firmado_url ?? undefined,
    guiasFirmadas: (viaje.guias_firmadas ?? []).map(mapGuiaFirmada),
    observaciones: viaje.observaciones ?? undefined,
    fechaInicio: viaje.fecha_inicio ? Date.parse(viaje.fecha_inicio) : undefined,
    fechaFin: viaje.fecha_fin ? Date.parse(viaje.fecha_fin) : undefined,
  };
}

function mapTransferencia(transferencia?: BackendViaje['transferencia']): TransferenciaViaje | undefined {
  if (!transferencia) return undefined;

  const totalUnidades = transferencia.total_unidades ?? transferencia.total_productos ?? undefined;
  const totalItems = transferencia.total_items ?? transferencia.items?.length ?? undefined;

  return {
    numero: transferencia.numero ?? undefined,
    fechaDocumento: transferencia.fecha_documento ?? undefined,
    bodegaOrigen: transferencia.bodega_origen ?? undefined,
    bodegaDestino: transferencia.bodega_destino ?? undefined,
    transportista: transferencia.transportista ?? undefined,
    vehiculo: transferencia.vehiculo ?? undefined,
    chofer: transferencia.chofer ?? undefined,
    descripcion: transferencia.descripcion ?? undefined,
    totalProductos: totalUnidades,
    totalItems,
    totalUnidades,
    items: (transferencia.items ?? []).map(item => ({
      codigo: item.codigo,
      codigoComercial: item.codigo_comercial ?? undefined,
      marcaRepuesto: item.marca_repuesto ?? undefined,
      producto: item.producto,
      cantidad: item.cantidad ?? undefined,
    })),
  };
}

function toBackendEstado(estado: Viaje['estado']): BackendViaje['estado'] {
  if (estado === 'finalizado') return 'completado';
  return estado;
}

function toBackendCoordinate(coord?: Coordenada) {
  if (!coord) return undefined;

  return {
    latitud: coord.lat,
    longitud: coord.lng,
  };
}

function sanitizeFileName(fileName: string): string {
  return fileName.replace(/[^A-Za-z0-9._-]+/g, '_').replace(/^_+|_+$/g, '') || 'archivo';
}

function inferExtensionFromMimeType(mimeType: string): string {
  const normalized = mimeType.toLowerCase();
  if (normalized === 'image/jpeg' || normalized === 'image/jpg') return 'jpg';
  if (normalized === 'image/png') return 'png';
  if (normalized === 'image/webp') return 'webp';
  if (normalized === 'image/heic') return 'heic';
  if (normalized === 'image/heif') return 'heif';
  return 'bin';
}

function normalizeUploadFile(file: File, prefix: string): File {
  const sanitizedName = sanitizeFileName(file.name || '');
  if (sanitizedName !== 'archivo' && /\.[A-Za-z0-9]+$/.test(sanitizedName)) {
    return file;
  }

  const extension = inferExtensionFromMimeType(file.type || '');
  const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

  return new File([file], `${prefix}-${timestamp}.${extension}`, {
    type: file.type || 'application/octet-stream',
    lastModified: file.lastModified || Date.now(),
  });
}

function buildPublicUrlFromPresignedUrl(presignedUrl: string): string {
  const url = new URL(presignedUrl);
  url.search = '';
  return url.toString();
}

function formatDashboardMeta(timestamp?: string | null): string {
  if (!timestamp) return '';

  const parsed = Date.parse(timestamp);
  if (Number.isNaN(parsed)) return '';

  return new Intl.DateTimeFormat('es-EC', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(parsed));
}

function mapDashboardFeedItem(item: BackendDashboardFeedItem): DashboardFeedItem {
  return {
    id: item.id,
    icon: item.icon,
    title: item.title,
    subtitle: item.subtitle,
    meta: formatDashboardMeta(item.timestamp),
    tone: item.tone,
  };
}

function mapDashboardStats(data: BackendDashboardStats): DashboardStatsData {
  return {
    summary: data.summary.map(item => ({
      icon: item.icon,
      label: item.label,
      value: item.value,
      trend: item.trend,
      trendTone: item.trend_tone ?? 'neutral',
    })),
    weeklySeries: data.weekly_series,
    recentActivity: data.recent_activity.map(mapDashboardFeedItem),
    notifications: data.notifications.map(mapDashboardFeedItem),
  };
}

export async function uploadFileToMinio(file: File, prefix: string): Promise<string> {
  const normalizedFile = normalizeUploadFile(file, prefix);
  const objectName = `${prefix}/${crypto.randomUUID()}-${sanitizeFileName(normalizedFile.name)}`;
  const response = await generarEnlaceSubidaMinio({ file_name: objectName });
  const presignedUrl = response.data;

  const uploadResponse = await fetch(presignedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': normalizedFile.type || 'application/octet-stream',
    },
    body: normalizedFile,
  });

  if (!uploadResponse.ok) {
    throw new Error('No se pudo subir el archivo a MinIO.');
  }

  return buildPublicUrlFromPresignedUrl(presignedUrl);
}

export function subscribeStorage(_listener?: () => void): () => void {
  return () => {
    return undefined;
  };
}

export function getConductores(params?: QueryParams): Promise<Conductor[]> {
  return apiClient
    .get<ApiEnvelope<BackendConductor[]>>('/api/v1/transporte/conductores/', params)
    .then(response => response.data.map(mapConductor));
}

export function createConductor(data: CreateConductorInput): Promise<Conductor> {
  return apiClient
    .post<ApiEnvelope<BackendConductor>>('/api/v1/transporte/conductores/', {
      existing_user_id: data.existingUserId,
      username: data.username,
      password: data.password,
      email: data.email,
      nombre: data.nombre,
      telefono: data.telefono,
      placa: data.placa,
      marca_vehiculo: data.marcaVehiculo,
      modelo_vehiculo: data.modeloVehiculo,
      anio_vehiculo: data.anioVehiculo,
    })
    .then(response => mapConductor(response.data));
}

export function getVehiculos(): Promise<Vehiculo[]> {
  return apiClient
    .get<ApiEnvelope<BackendVehiculo[]>>('/api/v1/transporte/vehiculos/')
    .then(response => response.data.map(mapVehiculo));
}

export function getSucursales(): Promise<Sucursal[]> {
  return apiClient
    .get<ApiEnvelope<BackendSucursal[]>>('/api/v1/transporte/sucursales/')
    .then(response => response.data.map(mapSucursal));
}

export function createSucursal(data: UpsertSucursalInput): Promise<Sucursal> {
  return apiClient
    .post<ApiEnvelope<BackendSucursal>>('/api/v1/transporte/sucursales/', {
      nombre: data.nombre,
      direccion: data.direccion,
      telefono: data.telefono,
    })
    .then(response => mapSucursal(response.data));
}

export function updateSucursal(id: string, data: UpsertSucursalInput): Promise<Sucursal> {
  return apiClient
    .put<ApiEnvelope<BackendSucursal>>(`/api/v1/transporte/sucursales/${id}/`, {
      nombre: data.nombre,
      direccion: data.direccion,
      telefono: data.telefono,
    })
    .then(response => mapSucursal(response.data));
}

export async function deleteSucursal(id: string): Promise<void> {
  await apiClient.delete<ApiEnvelope<null>>(`/api/v1/transporte/sucursales/${id}/`);
}

export function createVehiculo(data: UpsertVehiculoInput): Promise<Vehiculo> {
  return apiClient
    .post<ApiEnvelope<BackendVehiculo>>('/api/v1/transporte/vehiculos/', {
      placa: data.placa,
      marca: data.marca,
      modelo: data.modelo,
      anio: data.anio,
      tipo_gasolina: data.tipoGasolina,
      kilometros_galon: data.kilometrosGalon,
      precio_galon: data.precioGalon,
      etiqueta: data.etiqueta,
      sucursal_id: data.sucursalId ? Number(data.sucursalId) : null,
      conductor_id: data.conductorId ? Number(data.conductorId) : null,
    })
    .then(response => mapVehiculo(response.data));
}

export function updateVehiculo(id: string, data: UpsertVehiculoInput): Promise<Vehiculo> {
  return apiClient
    .put<ApiEnvelope<BackendVehiculo>>(`/api/v1/transporte/vehiculos/${id}/`, {
      placa: data.placa,
      marca: data.marca,
      modelo: data.modelo,
      anio: data.anio,
      tipo_gasolina: data.tipoGasolina,
      kilometros_galon: data.kilometrosGalon,
      precio_galon: data.precioGalon,
      etiqueta: data.etiqueta,
      sucursal_id: data.sucursalId ? Number(data.sucursalId) : null,
      conductor_id: data.conductorId ? Number(data.conductorId) : null,
    })
    .then(response => mapVehiculo(response.data));
}

export async function deleteVehiculo(id: string): Promise<void> {
  await apiClient.delete<ApiEnvelope<null>>(`/api/v1/transporte/vehiculos/${id}/`);
}

export async function getConductorByUserId(
  userId: number,
): Promise<Conductor | undefined> {
  try {
    const response = await apiClient.get<ApiEnvelope<BackendConductor>>(
      `/api/v1/transporte/conductores/user/${userId}/`,
    );
    return mapConductor(response.data);
  } catch (error) {
    const maybeError = error as { code?: number; message?: string };
    if (maybeError?.code === 404) return undefined;
    throw error;
  }
}

export async function deleteConductor(id: string): Promise<void> {
  await apiClient.delete<ApiEnvelope<null>>(`/api/v1/transporte/conductores/${id}/`);
}

function getViajesDesdeRuta(path: string, params?: QueryParams): Promise<Viaje[]> {
  return apiClient
    .get<ApiEnvelope<BackendViaje[]>>(path, params)
    .then(response => response.data.map(mapViaje));
}

function mapViajesPaginados(
  response: ApiEnvelope<{
    items: BackendViaje[];
    count: number;
    page: number;
    page_size: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  }>,
): PaginatedResult<Viaje> {
  return {
    items: response.data.items.map(mapViaje),
    count: response.data.count,
    page: response.data.page,
    pageSize: response.data.page_size,
    totalPages: response.data.total_pages,
    hasNext: response.data.has_next,
    hasPrevious: response.data.has_previous,
  };
}

export function getViajes(params?: QueryParams): Promise<Viaje[]> {
  return getViajesDesdeRuta('/api/v1/transporte/viajes/', params);
}

export function getViajesAsignadosPendientesConductor(params?: QueryParams): Promise<Viaje[]> {
  return getViajesDesdeRuta('/api/v1/transporte/viajes/asignados/pendientes/', params);
}

export function getViajesAsignadosEnCursoConductor(params?: QueryParams): Promise<Viaje[]> {
  return getViajesDesdeRuta('/api/v1/transporte/viajes/asignados/en-curso/', params);
}

export function getViajesPaginados(params?: QueryParams): Promise<PaginatedResult<Viaje>> {
  return apiClient
    .get<ApiEnvelope<{
      items: BackendViaje[];
      count: number;
      page: number;
      page_size: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    }>>('/api/v1/transporte/viajes/', {
      ...params,
      paginated: true,
    })
    .then(mapViajesPaginados);
}

export function getAdminDashboardStats(): Promise<DashboardStatsData> {
  return apiClient
    .get<ApiEnvelope<BackendDashboardStats>>('/api/v1/transporte/dashboard/admin/')
    .then(response => mapDashboardStats(response.data));
}

export function getConductorDashboardStats(): Promise<DashboardStatsData> {
  return apiClient
    .get<ApiEnvelope<BackendDashboardStats>>('/api/v1/transporte/dashboard/conductor/')
    .then(response => mapDashboardStats(response.data));
}

export function getVendedorDashboardStats(): Promise<DashboardStatsData> {
  return apiClient
    .get<ApiEnvelope<BackendDashboardStats>>('/api/v1/transporte/dashboard/vendedor/')
    .then(response => mapDashboardStats(response.data));
}

function mapProcessedGuiaResponse(data: ProcessedGuiaResponse): GuiaProcesada {
  return {
    guiaPdfUrl: data.guia_pdf_url,
    guiaPdfNombreArchivo: data.guia_pdf_nombre_archivo,
    cliente: mapCliente(data.cliente),
    suggestedUrlDestino: data.url_destino_sugerido || undefined,
    historicalDestinationUrls: (data.urls_destino_historicas ?? []).map(url => url.trim()).filter(Boolean),
    extraccion: {
      tipoDocumento: data.extraccion.tipo_documento,
      telefono: data.extraccion.telefono,
      direccion: data.extraccion.direccion,
      emisorRuc: data.extraccion.emisor_ruc,
      claveAcceso: data.extraccion.clave_acceso,
      subtotal: data.extraccion.subtotal,
      iva: data.extraccion.iva,
      total: data.extraccion.total,
      items: data.extraccion.items.map(item => ({
        codigo: item.codigo,
        descripcion: item.descripcion,
        cantidad: item.cantidad,
        precioUnitario: item.precio_unitario,
        precioTotal: item.precio_total,
      })),
      transferencia: data.extraccion.transferencia
        ? {
            numero: data.extraccion.transferencia.numero ?? '',
            fecha: data.extraccion.transferencia.fecha ?? '',
            bodegaOrigen: data.extraccion.transferencia.bodega_origen ?? '',
            bodegaDestino: data.extraccion.transferencia.bodega_destino ?? '',
            transportista: data.extraccion.transferencia.transportista ?? '',
            vehiculo: data.extraccion.transferencia.vehiculo ?? '',
            chofer: data.extraccion.transferencia.chofer ?? '',
            descripcion: data.extraccion.transferencia.descripcion ?? '',
            totalProductos: data.extraccion.transferencia.total_unidades ?? data.extraccion.transferencia.total_productos ?? undefined,
            totalItems: data.extraccion.transferencia.total_items ?? data.extraccion.transferencia.items?.length ?? undefined,
            totalUnidades: data.extraccion.transferencia.total_unidades ?? data.extraccion.transferencia.total_productos ?? undefined,
            items: (data.extraccion.transferencia.items ?? []).map(item => ({
              codigo: item.codigo,
              codigoComercial: item.codigo_comercial ?? undefined,
              marcaRepuesto: item.marca_repuesto ?? undefined,
              producto: item.producto,
              cantidad: item.cantidad ?? undefined,
            })),
          }
        : undefined,
      headerText: data.extraccion.header_text,
      bodyText: data.extraccion.body_text,
      footerText: data.extraccion.footer_text,
      textoExtraido: data.extraccion.texto_extraido,
    },
  };
}

export function procesarGuiaPdf(guia: GuiaFileUpload): Promise<GuiaProcesada> {
  return apiClient
    .post<ApiEnvelope<ProcessedGuiaResponse>>('/api/v1/transporte/guias/procesar/', {
      file_name: guia.fileName,
      content: guia.content,
      tipo_entrega: guia.tipoEntrega,
    })
    .then(response => mapProcessedGuiaResponse(response.data));
}

export function procesarTransferenciaExcel(guia: GuiaFileUpload): Promise<GuiaProcesada> {
  return apiClient
    .post<ApiEnvelope<ProcessedGuiaResponse>>('/api/v1/transporte/guias/procesar-transferencia-excel/', {
      file_name: guia.fileName,
      content: guia.content,
    })
    .then(response => mapProcessedGuiaResponse(response.data));
}

export function firmarGuiaPdf(input: {
  guiaPdfUrl: string;
  signatureContent: string;
  fileName?: string;
  pageNumber?: number;
  pdfX: number;
  pdfY: number;
  pdfWidth: number;
  pdfHeight: number;
}): Promise<GuiaFirmada> {
  return apiClient
    .post<ApiEnvelope<{
      pdf_guia_firmado_url: string;
      pdf_guia_firmado_nombre_archivo: string;
    }>>('/api/v1/transporte/guias/firmar/', {
      guia_pdf_url: input.guiaPdfUrl,
      signature_content: input.signatureContent,
      file_name: input.fileName,
      page_number: input.pageNumber,
      pdf_x: input.pdfX,
      pdf_y: input.pdfY,
      pdf_width: input.pdfWidth,
      pdf_height: input.pdfHeight,
    })
    .then(response => ({
      pdfGuiaFirmadoUrl: response.data.pdf_guia_firmado_url,
      pdfGuiaFirmadoNombreArchivo: response.data.pdf_guia_firmado_nombre_archivo,
    }));
}

export function verificarDestinoGoogleMaps(urlDestino: string): Promise<DestinoVerificado> {
  return apiClient
    .post<ApiEnvelope<DestinoVerificado>>('/api/v1/transporte/destinos/verificar/', {
      url_destino: urlDestino,
    })
    .then(response => response.data);
}

export async function getInicioDiaActual(conductorId?: string): Promise<InicioDia | undefined> {
  const response = await apiClient.get<ApiEnvelope<BackendInicioDia | null>>(
    '/api/v1/transporte/inicio-dia/actual/',
    conductorId ? { conductor_id: Number(conductorId) } : undefined,
  );
  return response.data ? mapInicioDia(response.data) : undefined;
}

export function createInicioDia(data: CreateInicioDiaInput): Promise<InicioDia> {
  return apiClient
    .post<ApiEnvelope<BackendInicioDia>>('/api/v1/transporte/inicio-dia/actual/', {
      conductor_id: data.conductorId ? Number(data.conductorId) : undefined,
      cantidad_gasolina: data.cantidadGasolina,
      precio_total: data.precioTotal,
    })
    .then(response => mapInicioDia(response.data));
}

export function createCargaGasolina(data: CreateCargaGasolinaInput): Promise<CargaGasolina> {
  return apiClient
    .post<ApiEnvelope<BackendCargaGasolina>>('/api/v1/transporte/cargas-gasolina/', {
      vehiculo_id: Number(data.vehiculoId),
      forma_pago: data.formaPago,
      ultimos_cuatro_tarjeta: data.ultimosCuatroTarjeta,
      numero_voucher: data.numeroVoucher,
      img_facturavou: data.imgFacturavou,
      cantidad_galones: data.cantidadGalones,
      total_pagado: data.totalPagado,
    })
    .then(response => mapCargaGasolina(response.data));
}

export function updateCargaGasolina(id: string, data: UpdateCargaGasolinaInput): Promise<CargaGasolina> {
  return apiClient
    .put<ApiEnvelope<BackendCargaGasolina>>(`/api/v1/transporte/cargas-gasolina/${id}/`, {
      vehiculo_id: Number(data.vehiculoId),
      forma_pago: data.formaPago,
      ultimos_cuatro_tarjeta: data.ultimosCuatroTarjeta,
      numero_voucher: data.numeroVoucher,
      cantidad_galones: data.cantidadGalones,
      total_pagado: data.totalPagado,
    })
    .then(response => mapCargaGasolina(response.data));
}

export interface AdminEditCargaGasolinaInput {
  formaPago: FormaPagoCargaGasolina;
  ultimosCuatroTarjeta?: string;
  numeroVoucher: string;
  cantidadGalones: number;
  totalPagado: number;
}

export function adminEditCargaGasolina(id: string, data: AdminEditCargaGasolinaInput): Promise<CargaGasolina> {
  return apiClient
    .put<ApiEnvelope<BackendCargaGasolina>>(`/api/v1/transporte/cargas-gasolina/${id}/editar/`, {
      forma_pago: data.formaPago,
      ultimos_cuatro_tarjeta: data.formaPago === 'tc' ? (data.ultimosCuatroTarjeta || '') : '',
      numero_voucher: data.numeroVoucher || '',
      cantidad_galones: data.cantidadGalones,
      total_pagado: data.totalPagado,
    })
    .then(response => mapCargaGasolina(response.data));
}

export function getCargasGasolina(params?: QueryParams): Promise<PaginatedResult<CargaGasolina>> {
  return apiClient
    .get<ApiEnvelope<{
      items: BackendCargaGasolina[];
      count: number;
      page: number;
      page_size: number;
      total_pages: number;
      has_next: boolean;
      has_previous: boolean;
    }>>('/api/v1/transporte/cargas-gasolina/', params)
    .then(response => ({
      items: response.data.items.map(mapCargaGasolina),
      count: response.data.count,
      page: response.data.page,
      pageSize: response.data.page_size,
      totalPages: response.data.total_pages,
      hasNext: response.data.has_next,
      hasPrevious: response.data.has_previous,
    }));
}

export interface ExportTaskResult {
  task_id: string;
  status: string;
  file_url?: string;
  file_name?: string;
  error?: string;
}

export function exportarCargasGasolina(formato: 'excel' | 'pdf', params?: Record<string, string>): Promise<ExportTaskResult> {
  const queryParams: Record<string, string> = { formato, ...(params || {}) };
  return apiClient
    .post<ApiEnvelope<ExportTaskResult>>('/api/v1/transporte/cargas-gasolina/exportar/', {}, queryParams)
    .then(response => response.data);
}

export function getExportarCargasGasolinaStatus(taskId: string): Promise<ExportTaskResult> {
  return apiClient
    .get<ApiEnvelope<ExportTaskResult>>(`/api/v1/transporte/cargas-gasolina/exportar/${taskId}/`)
    .then(response => response.data);
}

export async function descargarArchivoExportacion(taskId: string, fileName: string): Promise<void> {
  const { getAccessToken, API_BASE_URL } = await import('./client');
  const token = getAccessToken();
  const url = `${API_BASE_URL}/transporte/cargas-gasolina/exportar/${taskId}/descargar/`;

  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    throw new Error(`Error al descargar: ${response.statusText}`);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(blobUrl);
}

export function saveViaje(viaje: Viaje, options: SaveViajeOptions = {}): Promise<Viaje> {
  const guiaFuentes = options.guiasProcesadas ?? (options.guiaProcesada ? [options.guiaProcesada] : []);
  const partialPaymentType = viaje.paquete?.pago?.tipoPagoParcial?.trim();
  const packagePayment = viaje.paquete?.pago?.tipo
    ? {
        tipo: viaje.paquete.pago.tipo,
        monto: viaje.paquete.pago.monto,
        banco: viaje.paquete.pago.banco,
        numero_transferencia: viaje.paquete.pago.numeroTransferencia,
        numero_cheque: viaje.paquete.pago.numeroCheque,
        numero_nota_credito: viaje.paquete.pago.numeroNotaCredito,
        valor_nota_credito: viaje.paquete.pago.valorNotaCredito,
        tipo_pago_parcial: partialPaymentType || undefined,
        monto_pagado: viaje.paquete.pago.montoPagado,
        medio_pago: viaje.paquete.pago.tipo === 'completo' ? viaje.paquete.pago.medioPago || undefined : undefined,
      }
    : undefined;
  const packagePayload = {
    nombre: viaje.paquete?.nombre,
    descripcion: viaje.paquete?.descripcion,
    cliente: viaje.paquete?.cliente
      ? {
          nombre: viaje.paquete.cliente.nombre,
          numero: viaje.paquete.cliente.numero,
          fecha_documento: viaje.paquete.cliente.fechaDocumento,
          ciudad: viaje.paquete.cliente.ciudad,
          sucursal: viaje.paquete.cliente.sucursal,
          correo_electronico: viaje.paquete.cliente.correoElectronico,
          direccion: viaje.paquete.cliente.direccion,
          telefono: viaje.paquete.cliente.telefono,
          tipo_cliente: viaje.paquete.cliente.tipoCliente || undefined,
        }
      : undefined,
    pago: packagePayment,
    valor_total_factura: viaje.paquete?.valorTotalFactura,
    guia_pdf_url: viaje.paquete?.guiaPdfUrl,
    guia_pdf_nombre_archivo: viaje.paquete?.guiaPdfNombreArchivo,
    envio_pdf_url: viaje.paquete?.envioPdfUrl,
    envio_pdf_nombre_archivo: viaje.paquete?.envioPdfNombreArchivo,
    guias_pdf: options.fullEdit
      ? viaje.paquete?.guiasPdf?.map(guia => ({
          url: guia.url,
          nombre_archivo: guia.nombreArchivo,
          numero_factura: guia.numeroFactura,
          direccion: guia.direccion,
          valor_total: guia.valorTotal,
          es_devuelta: guia.esDevuelta,
          tipo_devolucion: guia.tipoDevolucion,
          devolucion_total: guia.devolucionTotal,
          items_devueltos: guia.itemsDevueltos?.map(item => ({
            codigo: item.codigo,
            descripcion: item.descripcion,
            cantidad: item.cantidad,
            precio_unitario: item.precioUnitario,
            precio_total: item.precioTotal,
          })),
          extraccion: guia.extraccion
            ? {
                total: guia.extraccion.total,
                items: guia.extraccion.items.map(item => ({
                  codigo: item.codigo,
                  descripcion: item.descripcion,
                  cantidad: item.cantidad,
                  precio_unitario: item.precioUnitario,
                  precio_total: item.precioTotal,
                })),
              }
            : undefined,
        }))
      : undefined,
    guia_fuente: options.guiaProcesada
      ? {
          file_name: options.guiaProcesada.fileName,
          numero_factura: options.guiaProcesada.numeroFactura,
          direccion: options.guiaProcesada.direccion,
          extraccion: {
            tipo_documento: options.guiaProcesada.extraccion.tipoDocumento,
            telefono: options.guiaProcesada.extraccion.telefono,
            direccion: options.guiaProcesada.extraccion.direccion,
            emisor_ruc: options.guiaProcesada.extraccion.emisorRuc,
            clave_acceso: options.guiaProcesada.extraccion.claveAcceso,
            subtotal: options.guiaProcesada.extraccion.subtotal,
            iva: options.guiaProcesada.extraccion.iva,
            total: options.guiaProcesada.extraccion.total,
            items: options.guiaProcesada.extraccion.items.map(item => ({
              codigo: item.codigo,
              descripcion: item.descripcion,
              cantidad: item.cantidad,
              precio_unitario: item.precioUnitario,
              precio_total: item.precioTotal,
            })),
            transferencia: options.guiaProcesada.extraccion.transferencia
              ? {
                  numero: options.guiaProcesada.extraccion.transferencia.numero,
                  fecha: options.guiaProcesada.extraccion.transferencia.fecha,
                  bodega_origen: options.guiaProcesada.extraccion.transferencia.bodegaOrigen,
                  bodega_destino: options.guiaProcesada.extraccion.transferencia.bodegaDestino,
                  transportista: options.guiaProcesada.extraccion.transferencia.transportista,
                  vehiculo: options.guiaProcesada.extraccion.transferencia.vehiculo,
                  chofer: options.guiaProcesada.extraccion.transferencia.chofer,
                  descripcion: options.guiaProcesada.extraccion.transferencia.descripcion,
                  total_productos: options.guiaProcesada.extraccion.transferencia.totalUnidades ?? options.guiaProcesada.extraccion.transferencia.totalProductos,
                  total_items: options.guiaProcesada.extraccion.transferencia.totalItems,
                  total_unidades: options.guiaProcesada.extraccion.transferencia.totalUnidades ?? options.guiaProcesada.extraccion.transferencia.totalProductos,
                  items: options.guiaProcesada.extraccion.transferencia.items.map(item => ({
                    codigo: item.codigo,
                    codigo_comercial: item.codigoComercial,
                    marca_repuesto: item.marcaRepuesto,
                    producto: item.producto,
                    cantidad: item.cantidad,
                  })),
                }
              : undefined,
            header_text: options.guiaProcesada.extraccion.headerText,
            body_text: options.guiaProcesada.extraccion.bodyText,
            footer_text: options.guiaProcesada.extraccion.footerText,
            texto_extraido: options.guiaProcesada.extraccion.textoExtraido,
          },
        }
      : undefined,
    guia_fuentes: guiaFuentes.map(guia => ({
      file_name: guia.fileName,
      numero_factura: guia.numeroFactura,
      direccion: guia.direccion,
      extraccion: {
        tipo_documento: guia.extraccion.tipoDocumento,
        telefono: guia.extraccion.telefono,
        direccion: guia.extraccion.direccion,
        emisor_ruc: guia.extraccion.emisorRuc,
        clave_acceso: guia.extraccion.claveAcceso,
        subtotal: guia.extraccion.subtotal,
        iva: guia.extraccion.iva,
        total: guia.extraccion.total,
        items: guia.extraccion.items.map(item => ({
          codigo: item.codigo,
          descripcion: item.descripcion,
          cantidad: item.cantidad,
          precio_unitario: item.precioUnitario,
          precio_total: item.precioTotal,
        })),
        transferencia: guia.extraccion.transferencia
          ? {
              numero: guia.extraccion.transferencia.numero,
              fecha: guia.extraccion.transferencia.fecha,
              bodega_origen: guia.extraccion.transferencia.bodegaOrigen,
              bodega_destino: guia.extraccion.transferencia.bodegaDestino,
              transportista: guia.extraccion.transferencia.transportista,
              vehiculo: guia.extraccion.transferencia.vehiculo,
              chofer: guia.extraccion.transferencia.chofer,
              descripcion: guia.extraccion.transferencia.descripcion,
              total_productos: guia.extraccion.transferencia.totalUnidades ?? guia.extraccion.transferencia.totalProductos,
              total_items: guia.extraccion.transferencia.totalItems,
              total_unidades: guia.extraccion.transferencia.totalUnidades ?? guia.extraccion.transferencia.totalProductos,
              items: guia.extraccion.transferencia.items.map(item => ({
                codigo: item.codigo,
                codigo_comercial: item.codigoComercial,
                marca_repuesto: item.marcaRepuesto,
                producto: item.producto,
                cantidad: item.cantidad,
              })),
            }
          : undefined,
        header_text: guia.extraccion.headerText,
        body_text: guia.extraccion.bodyText,
        footer_text: guia.extraccion.footerText,
        texto_extraido: guia.extraccion.textoExtraido,
      },
    })),
  };
  if (!/^\d+$/.test(viaje.id)) {
    return apiClient
      .post<ApiEnvelope<BackendViaje>>('/api/v1/transporte/viajes/', {
        conductor_id: viaje.conductorId ? Number(viaje.conductorId) : null,
        tipo_entrega: viaje.tipoEntrega,
        compania_transporte: viaje.companiaTransporte,
        valor_total_envio: viaje.valorTotalEnvio,
        fee_envio: viaje.feeEnvio,
        url_destino: viaje.urlDestino,
        puntos_intermedios: viaje.puntosIntermedios.map(point => ({
          url_destino: point.urlDestino,
        })),
        tiempo_estimado_minutos: viaje.tiempoEstimadoMinutos,
        paquete: packagePayload,
      })
      .then(response => mapViaje(response.data));
  }

  return apiClient
    .put<ApiEnvelope<BackendViaje>>(`/api/v1/transporte/viajes/${viaje.id}/`, {
      estado: toBackendEstado(viaje.estado),
      tipo_entrega: options.fullEdit ? viaje.tipoEntrega : undefined,
      compania_transporte: options.fullEdit ? viaje.companiaTransporte : undefined,
      valor_total_envio: options.fullEdit ? viaje.valorTotalEnvio : undefined,
      fee_envio: options.fullEdit ? viaje.feeEnvio : undefined,
      url_destino: options.fullEdit ? viaje.urlDestino : undefined,
      puntos_intermedios: options.fullEdit
        ? viaje.puntosIntermedios.map(point => ({
            url_destino: point.urlDestino,
          }))
        : undefined,
      tiempo_estimado_minutos: options.fullEdit ? viaje.tiempoEstimadoMinutos : undefined,
      paquete: options.fullEdit ? packagePayload : undefined,
      transferencia: options.fullEdit && viaje.transferencia
        ? {
            numero: viaje.transferencia.numero,
            fecha: viaje.transferencia.fechaDocumento,
            bodega_origen: viaje.transferencia.bodegaOrigen,
            bodega_destino: viaje.transferencia.bodegaDestino,
            transportista: viaje.transferencia.transportista,
            vehiculo: viaje.transferencia.vehiculo,
            chofer: viaje.transferencia.chofer,
            descripcion: viaje.transferencia.descripcion,
            total_productos: viaje.transferencia.totalUnidades ?? viaje.transferencia.totalProductos,
            total_items: viaje.transferencia.totalItems,
            total_unidades: viaje.transferencia.totalUnidades ?? viaje.transferencia.totalProductos,
            items: viaje.transferencia.items.map(item => ({
              codigo: item.codigo,
              codigo_comercial: item.codigoComercial,
              marca_repuesto: item.marcaRepuesto,
              producto: item.producto,
              cantidad: item.cantidad,
            })),
          }
        : undefined,
      coordenada_inicio: toBackendCoordinate(options.coordenadaInicio),
      coordenada_fin: toBackendCoordinate(options.coordenadaFin),
      paquete_imagenes: viaje.paquete?.imagenes,
      es_devolucion: viaje.esDevolucion,
      direccion_entrega: viaje.direccionEntrega,
      kilometros_recorridos: viaje.kilometrosRecorridos,
      forma_pago: viaje.formaPago,
      pago_entrega: viaje.pagoEntrega
        ? {
            tipo: viaje.pagoEntrega.tipo,
            monto: viaje.pagoEntrega.monto,
            banco: viaje.pagoEntrega.banco,
            numero_transferencia: viaje.pagoEntrega.numeroTransferencia,
            numero_cheque: viaje.pagoEntrega.numeroCheque,
            numero_nota_credito: viaje.pagoEntrega.numeroNotaCredito,
            valor_nota_credito: viaje.pagoEntrega.valorNotaCredito,
          }
        : undefined,
      referencia_pago: viaje.referenciaPago,
      imagen_entrega: viaje.imagenEntrega,
      imagenes_entrega: viaje.imagenesEntrega,
      pdf_guia_firmado_url: viaje.pdfGuiaFirmadoUrl,
      guias_firmadas: viaje.guiasFirmadas?.map(toBackendGuiaFirmada),
      guias_devolucion: options.guiasDevolucion?.map(toBackendGuiaDevolucion),
      gasolina: viaje.gasolina,
      pdf_guia_firmado: options.pdfGuiaFirmado
        ? {
            file_name: options.pdfGuiaFirmado.fileName,
            content: options.pdfGuiaFirmado.content,
          }
        : undefined,
      observaciones: viaje.observaciones,
    })
    .then(response => mapViaje(response.data));
}

export async function getViajeActivo(
  conductorId: string,
): Promise<Viaje | undefined> {
  const viajes = await getViajes();
  return viajes.find(
    v => (v.conductorId === conductorId || v.secondaryConductorId === conductorId) && v.estado !== 'finalizado',
  );
}

export function assignViaje(
  viajeId: string,
  conductorId: string,
  vehiculoId: string,
  secondaryConductorId?: string,
  secondaryVehiculoId?: string,
): Promise<Viaje> {
  return apiClient
    .post<ApiEnvelope<BackendViaje>>(`/api/v1/transporte/viajes/${viajeId}/asignar/`, {
      conductor_id: Number(conductorId),
      conductor_secundario_id: secondaryConductorId ? Number(secondaryConductorId) : null,
      vehiculo_id: Number(vehiculoId),
      vehiculo_secundario_id: secondaryVehiculoId ? Number(secondaryVehiculoId) : null,
    })
    .then(response => mapViaje(response.data));
}

export async function getViajeById(id: string): Promise<Viaje | undefined> {
  try {
    const response = await apiClient.get<ApiEnvelope<BackendViaje>>(
      `/api/v1/transporte/viajes/${id}/`,
    );
    return mapViaje(response.data);
  } catch (error) {
    const maybeError = error as { code?: number };
    if (maybeError?.code === 404) return undefined;
    throw error;
  }
}

export async function agregarCoordenada(
  viajeId: string,
  coord: Coordenada,
): Promise<Viaje | undefined> {
  const response = await apiClient.post<ApiEnvelope<BackendViaje>>(
    `/api/v1/transporte/viajes/${viajeId}/coordenadas/`,
    {
      latitud: coord.lat,
      longitud: coord.lng,
    },
  );

  return mapViaje(response.data);
}
